import { defineTransform, fromJson, JsonResponse, Nullable } from '@client/core/data/concerns/parses-from-json';
import { parseISO } from '@client/core/utils/date';
import { MlsHistory } from '@client/features/property-search/models/mls-history';
import { SaleHistory } from '@client/features/property-search/models/sale-history';
import { QueryAddress, QueryResult } from '@client/features/property-search/types';
import { $t } from '@client/i18n/t';

export class PropertySearch {
  constructor(
    public readonly id: number,
    public readonly ownerId: number,
    public readonly address: string,
    public readonly city: string,
    public readonly state: string,
    public readonly zip: string,
    public readonly latitude: number,
    public readonly longitude: number,
    public readonly beds: Nullable<number>,
    public readonly fullBaths: Nullable<number>,
    public readonly halfBaths: Nullable<number>,
    public readonly baths: Nullable<number>,
    public readonly sqFt: string,
    public readonly structureSqFt: string,
    public readonly status: string,
    public readonly ownerType: string,
    public readonly absenteeOwner: boolean,
    public readonly corporateOwned: boolean,
    public readonly forclosure: boolean,
    public readonly bankruptcy: boolean,
    public readonly highEquity: boolean,
    public readonly distressed: boolean,
    public readonly motivatedSellerIndicators: string[],
    public readonly occupancy: string,
    public readonly propertyType: string,
    public readonly propertyStyle: string,
    public readonly parking: string,
    public readonly yearBuilt: string,
    public readonly heating: string,
    public readonly cooling: string,
    public readonly water: string,
    public readonly sewer: string,
    public readonly constructionType: string,
    public readonly roofing: string,
    public readonly exteriorWalls: string,
    public readonly foundation: string,
    public readonly garageParkingCount: Nullable<number>,
    public readonly numberOfRooms: Nullable<number>,
    public readonly numberOfStories: Nullable<number>,
    public readonly numberOfUnits: Nullable<number>,
    public readonly lotSizeInAcres: string,
    public readonly lotFrontageInFeet: string,
    public readonly lotDepthInFeet: string,
    public readonly lotUse: string,
    public readonly estimatedValue: Nullable<number>,
    public readonly annualPropertyTaxAmount: Nullable<number>,
    public readonly assessedImprovementValue: Nullable<number>,
    public readonly assessedLandValue: Nullable<number>,
    public readonly assessedValueYear: Nullable<number>,
    public readonly saleHistory: SaleHistory[],
    public readonly mlsHistory: Nullable<MlsHistory>,
    public readonly createdAt: Date,
  ) {}
}

defineTransform(PropertySearch, (json: JsonResponse, { nullOr }) => {
  const query: QueryAddress = json.query || {};
  const result: QueryResult = json.result || {};

  return {
    id: json.id,
    ownerId: json.owner_id,
    address: query.address,
    city: query.city,
    state: query.state,
    zip: query.zip,
    latitude: parseFloat(result.latitude),
    longitude: parseFloat(result.longitude),
    beds: nullOr(result.bedrooms, () => parseInt(result.bedrooms)),
    fullBaths: getFullBaths(result),
    halfBaths: getHalfBaths(result),
    baths: getTotalBaths(result),
    sqFt: result.sqFt,
    structureSqFt: result.structureSqFt,
    status: result.activeListing ? $t('propertySearch.status.activeListing') : $t('propertySearch.status.offMarket'),
    absenteeOwner: result.absenteeOwner,
    corporateOwned: result.corporateOwned,
    forclosure: result.forclosure,
    bankruptcy: result.bankruptcy,
    highEquity: result.highEquity,
    distressed:
      result.activeForeclosure ||
      result.bankruptcy ||
      result.divorce ||
      result.inherited ||
      result.liens ||
      result.noticeOfDefault ||
      result.preForeclosure ||
      result.taxDefault ||
      result.tiredLandlord ||
      result.vacant,
    ownerType: result.corporateOwned
      ? $t('propertySearch.ownerType.corporate')
      : $t('propertySearch.ownerType.individual'),
    motivatedSellerIndicators: getMotivatedSellerIndicators(result),
    occupancy: result.vacant ? $t('propertySearch.occupancy.vacant') : $t('propertySearch.occupancy.occupied'),
    propertyType: result.propertyType,
    propertyStyle: result.propertyStyle,
    parking: result.parking,
    yearBuilt: result.yearBuilt,
    heating: result.heating,
    cooling: result.cooling,
    water: result.water,
    sewer: result.sewer,
    constructionType: result.constructionType,
    roofing: result.roofing,
    exteriorWalls: result.facade,
    foundation: result.foundation,
    garageParkingCount: nullOr(result.garageParkingCount, () => parseInt(result.garageParkingCount)),
    numberOfRooms: nullOr(result.numberOfRooms, () => parseInt(result.numberOfRooms)),
    numberOfStories: nullOr(result.numberOfStories, () => parseInt(result.numberOfStories)),
    numberOfUnits: nullOr(result.numberOfUnits, () => parseInt(result.numberOfUnits)),
    lotSizeInAcres: result.acres,
    lotFrontageInFeet: result.lotFrontage,
    lotDepthInFeet: result.lotDepth,
    lotUse: result.landUse,
    annualPropertyTaxAmount: nullOr(result.annualPropertyTaxAmount, () => parseInt(result.annualPropertyTaxAmount)),
    assessedImprovementValue: nullOr(result.assessedImprovementValue, () => parseInt(result.assessedImprovementValue)),
    assessedLandValue: nullOr(result.assessedLandValue, () => parseInt(result.assessedLandValue)),
    assessedValueYear: nullOr(result.assessedValueYear, () => parseInt(result.assessedValueYear)),
    estimatedValue: nullOr(result.assessedValueTotal, () => parseInt(result.assessedValueTotal)),
    saleHistory: (result.saleHistory || []).map((history) => fromJson(SaleHistory, history)),
    mlsHistory: nullOr(result.mlsHistory, () => fromJson(MlsHistory, result.mlsHistory)),
    createdAt: nullOr(json.created_at, () => parseISO(json.created_at)),
  };
});

const getMotivatedSellerIndicators = (result: QueryResult): string[] => {
  return [
    result.absenteeOwner && $t('propertySearch.motivatedSellerIndicators.vacant'),
    result.vacant && $t('propertySearch.motivatedSellerIndicators.vacant'),
    result.outOfStateOwner && $t('propertySearch.motivatedSellerIndicators.absenteeOwner'),
    result.bankruptcy && $t('propertySearch.motivatedSellerIndicators.bankruptcy'),
    result.cashBuyer && $t('propertySearch.motivatedSellerIndicators.cashBuyer'),
    result.corporateOwned && $t('propertySearch.motivatedSellerIndicators.corporateOwned'),
    result.divorce && $t('propertySearch.motivatedSellerIndicators.Divorce'),
    result.expiredListing && $t('propertySearch.motivatedSellerIndicators.expiredListing'),
    result.isFreeAndClear && $t('propertySearch.motivatedSellerIndicators.freeAndClear'),
    result.highEquity && $t('propertySearch.motivatedSellerIndicators.highEquity'),
    result.inherited && $t('propertySearch.motivatedSellerIndicators.inherited'),
    result.liens && $t('propertySearch.motivatedSellerIndicators.mortgagesLiens'),
    result.lowEquity && $t('propertySearch.motivatedSellerIndicators.lowEquity'),
    result.tiredLandlord && $t('propertySearch.motivatedSellerIndicators.tiredLandlord'),
    result.foreclosure && $t('propertySearch.motivatedSellerIndicators.foreclosure'),
    result.preForeclosure && $t('propertySearch.motivatedSellerIndicators.preForeclosure'),
    result.noticeOfDefault && $t('propertySearch.motivatedSellerIndicators.noticeOfDefault'),
    result.taxDefault && $t('propertySearch.motivatedSellerIndicators.taxDefault'),
  ].filter(Boolean);
};

const getFullBaths = (result: QueryResult): number | null => {
  const parsed = parseInt(result.fullBathCount);
  return isNaN(parsed) ? null : parsed;
};

const getHalfBaths = (result: QueryResult): number | null => {
  const parsed = parseInt(result.halfBathCount);
  return isNaN(parsed) ? null : parsed;
};

const getTotalBaths = (result: QueryResult): number | null => {
  const fullBaths = getFullBaths(result);
  const halfBaths = getHalfBaths(result);

  if (fullBaths === null && halfBaths === null) {
    return null;
  } else if (fullBaths === null) {
    return halfBaths;
  } else if (halfBaths === null) {
    return fullBaths;
  } else {
    return fullBaths + halfBaths;
  }
};
